import { ColumnFiltersState } from "@tanstack/table-core";
import dayjs from "dayjs";

import {
  HhAggregatedRowDto,
  HhFieldFilterDto,
  HhFieldFilterDtoFieldEnum,
  HhFieldFilterDtoModeEnum,
  QueryHandHistoryAggregatedRequestDtoGroupByEnum,
} from "@/shared/api";

export const SUMMARY_ROW_ID = "__summary__";

export const COLUMN_TO_GROUP_BY: Record<
  string,
  QueryHandHistoryAggregatedRequestDtoGroupByEnum
> = {
  room: QueryHandHistoryAggregatedRequestDtoGroupByEnum.Room,
  date: QueryHandHistoryAggregatedRequestDtoGroupByEnum.Date,
  pid: QueryHandHistoryAggregatedRequestDtoGroupByEnum.Pid,
  start_time_ms: QueryHandHistoryAggregatedRequestDtoGroupByEnum.StartTime,
  profile: QueryHandHistoryAggregatedRequestDtoGroupByEnum.Profile,
  tournament: QueryHandHistoryAggregatedRequestDtoGroupByEnum.Tournament,
  buyin_usd: QueryHandHistoryAggregatedRequestDtoGroupByEnum.BuyinUsd,
  game_type: QueryHandHistoryAggregatedRequestDtoGroupByEnum.GameType,
};

export const groupingToGroupBy = (
  grouping: string[]
): QueryHandHistoryAggregatedRequestDtoGroupByEnum[] =>
  grouping
    .map((columnId) => COLUMN_TO_GROUP_BY[columnId])
    .filter((value): value is QueryHandHistoryAggregatedRequestDtoGroupByEnum => !!value);

export type MttAnalyticsRow = HhAggregatedRowDto & {
  _isSummary?: boolean;
  _rowNumber?: number;
  reentry?: number;
  place?: number;
  starting_stack_bb?: number;
};

const COLUMN_TO_FIELD: Record<string, HhFieldFilterDtoFieldEnum> = {
  pid: HhFieldFilterDtoFieldEnum.Pid,
  room: HhFieldFilterDtoFieldEnum.Room,
  profile: HhFieldFilterDtoFieldEnum.Profile,
  start_time_ms: HhFieldFilterDtoFieldEnum.StartTime,
  buyin_usd: HhFieldFilterDtoFieldEnum.BuyinUsd,
  tournament: HhFieldFilterDtoFieldEnum.Tournament,
  game_type: HhFieldFilterDtoFieldEnum.GameType,
  date: HhFieldFilterDtoFieldEnum.Date,
};

const FILTER_FN_TO_MODE: Record<string, HhFieldFilterDtoModeEnum> = {
  equals: HhFieldFilterDtoModeEnum.Equals,
  notEquals: HhFieldFilterDtoModeEnum.NotEquals,
  between: HhFieldFilterDtoModeEnum.Between,
  contains: HhFieldFilterDtoModeEnum.Contains,
  startsWith: HhFieldFilterDtoModeEnum.StartsWith,
  endsWith: HhFieldFilterDtoModeEnum.EndsWith,
  empty: HhFieldFilterDtoModeEnum.Empty,
  notEmpty: HhFieldFilterDtoModeEnum.NotEmpty,
  fuzzy: HhFieldFilterDtoModeEnum.Contains,
  greaterThan: HhFieldFilterDtoModeEnum.Between,
  lessThan: HhFieldFilterDtoModeEnum.Between,
};

const normalizeFilterValue = (field: HhFieldFilterDtoFieldEnum, value: unknown): string => {
  const str = String(value ?? "").trim();
  if (!str) {
    return "";
  }

  if (field === HhFieldFilterDtoFieldEnum.StartTime) {
    const parsed = dayjs(str);
    if (parsed.isValid()) {
      return String(parsed.valueOf());
    }
  }

  if (field === HhFieldFilterDtoFieldEnum.Date) {
    const parsed = dayjs(str);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }
  }

  return str;
};

type SummaryType = "none" | "sum" | "avg";

const SUMMARY_CONFIG: Record<string, SummaryType> = {
  rowNumber: "none",
  pid: "none",
  count: "none",
  hand_count: "sum",
  start_time_ms: "none",
  buyin_usd: "none",
  sum_buyin_usd: "sum",
  avg_buyin_usd: "avg",
  sum_rebuy_usd: "sum",
  count_rebuy: "sum",
  sum_addon_usd: "sum",
  count_addon: "sum",
  reentry: "none",
  profit_usd: "sum",
  room: "none",
  contributions_usd: "sum",
  prize_usd: "sum",
  rake_usd: "sum",
  roi_pct: "none",
  itm_pct: "none",
  place: "none",
  avg_num_players: "avg",
  starting_stack_bb: "avg",
};

const round = (value: unknown) => Number(parseFloat(String(value)).toFixed(2));

const sumField = (rows: HhAggregatedRowDto[], key: keyof HhAggregatedRowDto) =>
  rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);

const avgField = (rows: HhAggregatedRowDto[], key: keyof HhAggregatedRowDto) => {
  if (rows.length === 0) {
    return 0;
  }
  return sumField(rows, key) / rows.length;
};

export const columnFiltersToHhFilters = (
  columnFilters: ColumnFiltersState,
  columnFilterFns: Record<string, string>
): HhFieldFilterDto[] =>
  columnFilters.flatMap((filter) => {
    const field = COLUMN_TO_FIELD[filter.id];
    if (!field) {
      return [];
    }

    const filterFn = columnFilterFns[filter.id] || "equals";
    const mode = FILTER_FN_TO_MODE[filterFn] || HhFieldFilterDtoModeEnum.Equals;

    if (filterFn === "empty" || filterFn === "notEmpty") {
      return [
        {
          field,
          mode,
          value: "",
          value2: "",
        },
      ];
    }

    if (Array.isArray(filter.value)) {
      const value = normalizeFilterValue(field, filter.value[0]);
      const value2 = normalizeFilterValue(field, filter.value[1]);

      if (!value && !value2) {
        return [];
      }

      if (filterFn === "greaterThan") {
        return [
          {
            field,
            mode: HhFieldFilterDtoModeEnum.Between,
            value,
            value2: "",
          },
        ];
      }

      if (filterFn === "lessThan") {
        return [
          {
            field,
            mode: HhFieldFilterDtoModeEnum.Between,
            value: "",
            value2: value,
          },
        ];
      }

      return [
        {
          field,
          mode,
          value,
          value2,
        },
      ];
    }

    const value = normalizeFilterValue(field, filter.value);
    if (!value) {
      return [];
    }

    return [
      {
        field,
        mode,
        value,
        value2: "",
      },
    ];
  });

export type MttAggregatedValues = {
  hand_count: number;
  avg_buyin_usd: number;
  sum_buyin_usd: number;
  count_rebuy: number;
  sum_rebuy_usd: number;
  count_addon: number;
  sum_addon_usd: number;
  profit_usd: number;
  contributions_usd: number;
  prize_usd: number;
  rake_usd: number;
  avg_num_players: number;
};

export const calculateAggregatedValues = (
  rows: HhAggregatedRowDto[]
): MttAggregatedValues => ({
  hand_count: round(sumField(rows, "hand_count")),
  avg_buyin_usd: round(avgField(rows, "avg_buyin_usd")),
  sum_buyin_usd: round(sumField(rows, "sum_buyin_usd")),
  count_rebuy: round(sumField(rows, "count_rebuy")),
  sum_rebuy_usd: round(sumField(rows, "sum_rebuy_usd")),
  count_addon: round(sumField(rows, "count_addon")),
  sum_addon_usd: round(sumField(rows, "sum_addon_usd")),
  profit_usd: round(sumField(rows, "profit_usd")),
  contributions_usd: round(sumField(rows, "contributions_usd")),
  prize_usd: round(sumField(rows, "prize_usd")),
  rake_usd: round(sumField(rows, "rake_usd")),
  avg_num_players: round(avgField(rows, "avg_num_players")),
});

export const SUMMARY_MARKER_ROW: MttAnalyticsRow = {
  room: "",
  date: "",
  pid: "",
  start_time_ms: 0,
  profile: "",
  tournament: "",
  buyin_usd: 0,
  game_type: "",
  count: 0,
  hand_count: 0,
  avg_buyin_usd: 0,
  sum_buyin_usd: 0,
  count_rebuy: 0,
  sum_rebuy_usd: 0,
  count_addon: 0,
  sum_addon_usd: 0,
  profit_usd: 0,
  contributions_usd: 0,
  prize_usd: 0,
  rake_usd: 0,
  roi_pct: 0,
  itm_pct: 0,
  avg_num_players: 0,
  _isSummary: true,
};

export const formatNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return round(value);
};

export const formatPercent = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return `${round(value)}%`;
};

export const getAggregatedCellValue = (
  columnId: string,
  aggregated: MttAggregatedValues
): string | number => {
  const type = SUMMARY_CONFIG[columnId];
  if (!type || type === "none") {
    return "";
  }

  const value = aggregated[columnId as keyof MttAggregatedValues];
  if (value === null || value === undefined) {
    return "";
  }

  if (columnId === "roi_pct" || columnId === "itm_pct") {
    return formatPercent(value);
  }

  return formatNumber(value);
};
